import { get, isEmpty } from 'lodash';
import { GetServerSidePropsContext, InferGetServerSidePropsType } from 'next';
import Skeleton from 'react-loading-skeleton';
import Breadcrumb from '~/components/Breadcrumb';
import Container from '~/components/Container';
import SEOTags from '~/components/SEOTags';
import SkeletonByTimeout from '~/components/Skeletons/SkeletonByTimeout';
import TableOfContent from '~/components/TableOfContent';
import Text from '~/components/Text';
import { PATH } from '~/constants/enum';
import { QUERY_GET_CMS_PAGE } from '~/data/cms/gql';
import { useReplaceCMS } from '~/hooks/useReplaceCMS';
import Layout from '~/layouts/ThemeLayout';
import { initializeSSRApollo } from '~/utils/apollo-client';

const getText = (domNode: any) => {
    let text = '';
    domNode?.children?.forEach((child: any) => {
        if (child.type === 'text') {
            text += child.data;
        } else {
            text += getText(child);
        }
    });
    return text;
};

const CmsPage = ({ data, loading }: InferGetServerSidePropsType<typeof getServerSideProps>) => {
    const cmsPage = get(data, 'cmsPage.content', '') as string;

    const { content, tableContent } = useReplaceCMS({
        data: cmsPage,
        loading,
        replace: (domNode: any) => {
            if (domNode?.class === 'h2') {
                const textH2 = getText(domNode);
                return (
                    <Text type="headline-2" className="text-center mb-2">
                        {textH2}
                    </Text>
                );
            } else if (domNode.name === 'h3') {
                return (
                    <Text type="title-3" className="text-primary" htmlTag="h2">
                        {domNode.children?.[0].data}
                    </Text>
                );
            }
        }
    });

    const { meta_description, meta_title, meta_keywords, url_key, title } = data?.cmsPage || {};

    return (
        <Layout>
            <SEOTags title={meta_title} description={meta_description} keywords={meta_keywords} url={url_key} />
            <Container rootClassName="py-10 lg:py-16" className="flex flex-col gap-8 cms-page">
                <Breadcrumb items={[{ title: 'Home', path: '/' }, { title: title }]} />
                <div className="flex flex-col-reverse gap-8 lg:flex-row">
                    <div className="flex flex-col gap-8 " style={{ width: '-webkit-fill-available' }}>
                        {data
                            ? content
                            : Array(5)
                                .fill('')
                                .map((_, index) => {
                                    return (
                                        <div key={index} className="flex flex-col gap-5">
                                            <SkeletonByTimeout>
                                                <Skeleton height={32} />
                                            </SkeletonByTimeout>
                                            <div className="flex flex-col gap-3">
                                                {Array(3)
                                                    .fill('')
                                                    .map((_, cIndex) => {
                                                        return (
                                                            <SkeletonByTimeout key={cIndex}>
                                                                <Skeleton height={20} />
                                                            </SkeletonByTimeout>
                                                        );
                                                    })}
                                            </div>
                                        </div>
                                    );
                                })}
                    </div>
                    {!isEmpty(tableContent) && <TableOfContent headings={tableContent.map((item) => item?.title)} />}
                </div>
            </Container>
        </Layout>
    );
};

export default CmsPage;

export async function getServerSideProps({ params }: GetServerSidePropsContext) {
    const { cmsPage: uri } = params || {};
    const apolloClient = initializeSSRApollo({
        httpLinkProps: { useGETForQueries: true }
    });

    let res = {} as CmsPageProps;
    const slug: string = (uri as string) || '';
    if (slug.includes('sneaker-release')) {
        return {
            notFound: true // Prevent sneaker-release routes from being handled by this page
        };
    }
    try {
        const arrURL = slug?.split('-');
        const defaultIndex = arrURL?.length || 1;
        let detectSKU = arrURL[defaultIndex];
        for (let index = defaultIndex - 1; index >= 0; index--) {
            const regex = /^\d{12}(-p)?$/;

            if (regex.test(arrURL[index])) {
                if (index != arrURL?.length) {
                    detectSKU = arrURL[index] + (arrURL[index + 1] ? `-${arrURL[index + 1]}` : '');
                    break;
                }
                detectSKU = arrURL[index];
                break;
            }
        }
        if (!!detectSKU) {
            return {
                redirect: {
                    destination: PATH.ProductDetail.replace('[slug]', slug),
                    statusCode: 301
                }
            };
        }
        if (!!uri) {
            const data = await apolloClient.query({
                query: QUERY_GET_CMS_PAGE,
                variables: {
                    identifier: uri
                },
                context: { fetchOptions: { method: 'GET' } }
            });

            res = data;
        }
    } catch (error) {
        console.error(error, 'QUERY_SEO_CATEGORY');
    }

    if (isEmpty(res)) {
        return {
            notFound: true
        };
    }

    return { props: { uri, ...res } };
}
